import { COLLECTIONS } from '../../constants/collections';
import { RESERVATION_WORKFLOW_ACTIONS } from '../../constants/systemConfig';
import { createDocument, getDocument, updateDocument, getAllDocuments } from '../firestore/firestoreService';
import { VEHICLE_STATUS } from '../../constants/statuses';

/**
 * Reservation Workflow Logger - Rezervasyon iş akışı takibi
 */

const getCollectionPath = (tenantId) => `tenants/${tenantId}/${COLLECTIONS.RESERVATION_WORKFLOW_LOGS}`;

// Create new reservation workflow log
export const createReservationWorkflowLog = async (reservationId, tenantId, initialStatus) => {
    if (!tenantId) throw new Error('Tenant ID is required for workflow log');

    const data = {
        reservationId,
        tenantId,
        history: [],
        currentStatus: initialStatus || VEHICLE_STATUS.PENDING_APPROVAL,
        createdAt: new Date(),
        updatedAt: new Date()
    };

    return await createDocument(getCollectionPath(tenantId), data);
};

// Add workflow action to log
export const logReservationAction = async (logId, actionData, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required to log action');

    const log = await getDocument(getCollectionPath(tenantId), logId);

    if (!log) {
        throw new Error('Rezervasyon workflow logu bulunamadı');
    }

    const action = {
        action: actionData.action,
        timestamp: new Date(),
        performedBy: actionData.performedBy,
        performedByRole: actionData.performedByRole || null,
        previousStatus: log.currentStatus,
        newStatus: actionData.newStatus || log.currentStatus,
        changes: actionData.changes || {},
        notes: actionData.notes || null
    };

    const updatedHistory = [...log.history, action];

    return await updateDocument(getCollectionPath(tenantId), logId, {
        history: updatedHistory,
        currentStatus: action.newStatus,
        updatedAt: new Date()
    });
};

// Get reservation workflow log by reservation ID
export const getReservationWorkflowLogByReservation = async (reservationId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const conditions = [
        { type: 'where', field: 'reservationId', operator: '==', value: reservationId }
    ];

    const results = await getAllDocuments(getCollectionPath(tenantId), conditions);
    return results.length > 0 ? results[0] : null;
};

// Get reservation workflow log by ID
export const getReservationWorkflowLog = async (logId, tenantId) => {
    if (!tenantId) throw new Error('Tenant ID is required');
    return await getDocument(getCollectionPath(tenantId), logId);
};

// Get all workflow logs for a tenant
export const getReservationWorkflowLogs = async (tenantId, limit = 100) => {
    if (!tenantId) throw new Error('Tenant ID is required');

    const conditions = [
        // { type: 'where', field: 'tenantId', operator: '==', value: tenantId }, // Redundant in subcollection
        { type: 'orderBy', field: 'updatedAt', direction: 'desc' },
        { type: 'limit', value: limit }
    ];

    return await getAllDocuments(getCollectionPath(tenantId), conditions);
};

// ============ HELPER FUNCTIONS ============

// Log reservation created
export const logReservationCreated = async (reservationId, tenantId, performedBy, reservationData) => {
    const logId = await createReservationWorkflowLog(reservationId, tenantId, VEHICLE_STATUS.PENDING_APPROVAL);
    // getDocument also needs tenantId
    const log = await getDocument(getCollectionPath(tenantId), logId);

    return await logReservationAction(log.id, {
        action: RESERVATION_WORKFLOW_ACTIONS.CREATED,
        performedBy,
        performedByRole: 'planner',
        newStatus: VEHICLE_STATUS.PENDING_APPROVAL,
        changes: reservationData
    }, tenantId);
};

// Log submitted to transporter
export const logSubmittedToTransporter = async (logId, performedBy, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.SUBMITTED_TO_TRANSPORTER,
        performedBy,
        performedByRole: 'system',
        newStatus: 'submitted_to_transporter' // Workflow specific
    }, tenantId);
};

// Log approved by transporter
export const logApprovedByTransporter = async (logId, performedBy, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.APPROVED_BY_TRANSPORTER,
        performedBy,
        performedByRole: 'transporter_manager',
        newStatus: VEHICLE_STATUS.APPROVED
    }, tenantId);
};

// Log revision requested
export const logRevisionRequested = async (logId, performedBy, changes, reason, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.REVISION_REQUESTED,
        performedBy,
        performedByRole: 'transporter_manager',
        newStatus: 'pending_planner_review', // Workflow specific
        changes,
        notes: reason
    }, tenantId);
};

// Log driver assigned
export const logDriverAssigned = async (logId, performedBy, driverInfo, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.DRIVER_ASSIGNED,
        performedBy,
        performedByRole: 'transporter_manager',
        changes: { driverInfo }
    }, tenantId);
};

// Log plate assigned
export const logPlateAssigned = async (logId, performedBy, plateNumber, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.PLATE_ASSIGNED,
        performedBy,
        performedByRole: 'transporter_manager',
        changes: { plateNumber }
    }, tenantId);
};

// Log planner approved revision
export const logPlannerApprovedRevision = async (logId, performedBy, notes, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.PLANNER_APPROVED_REVISION,
        performedBy,
        performedByRole: 'planner',
        newStatus: VEHICLE_STATUS.APPROVED,
        notes
    }, tenantId);
};

// Log planner rejected revision
export const logPlannerRejectedRevision = async (logId, performedBy, notes, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.PLANNER_REJECTED_REVISION,
        performedBy,
        performedByRole: 'planner',
        newStatus: VEHICLE_STATUS.CANCELLED,
        notes
    }, tenantId);
};

// Log cancelled
export const logReservationCancelled = async (logId, performedBy, reason, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.CANCELLED,
        performedBy,
        newStatus: VEHICLE_STATUS.CANCELLED,
        notes: reason
    }, tenantId);
};

// Log completed
export const logReservationCompleted = async (logId, performedBy, tenantId) => {
    return await logReservationAction(logId, {
        action: RESERVATION_WORKFLOW_ACTIONS.COMPLETED,
        performedBy,
        newStatus: VEHICLE_STATUS.LOADING_COMPLETED
    }, tenantId);
};
